import { useState, useEffect, useRef, useMemo } from 'react'
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  Container,
  Snackbar,
  Stack,
  Tooltip,
  LinearProgress,
  MenuItem,
  Tabs,
  Tab,
  Divider,
} from '@mui/material'
import {
  Add,
  Edit,
  Visibility,
  Groups,
  Delete as DeleteIcon,
  CheckCircle,
  Error as ErrorIcon,
  CloudUpload,
  Download,
  Movie,
  Save,
} from '@mui/icons-material'
import { useAppSelector } from '../hooks/redux'
import {
  useGetTeamsQuery,
  useCreateTeamMutation,
  useUpdateTeamMutation,
  useDeleteTeamMutation,
  useReviewTeamMutation,
  useGetTeamAutoReviewQuery,
  useRevokeAutoApprovalMutation,
  useUploadMemberCardMutation,
  useUploadTeamProfileFileMutation,
  useUploadTeamIntroVideoMutation,
  useLazyDownloadTeamProfileQuery,
  useLazyStreamTeamIntroVideoQuery,
  useGetTeamCasesQuery,
  type Team,
  type TeamCreate,
  type MemberCardInfo,
  type MemberDetails,
  type TeamFileInfo,
  type TeamVideoInfo,
} from '../store/api/teamsApi'
import { useLazyGetUserByEmailQuery, useGetUsersBatchQuery, useFetchUsersBatchMutation } from '../store/api/usersApi'
import { hasPermission } from '../utils/permissions'
import { gradientBrand } from '../theme/brand'
import { PageHeader } from '../components/PageHeader'
import { getTablePaperSx } from '../theme/sectionSurfaces'
import {
  getPriorityRegistrationIssues,
  getTeamRegistrationColor,
  getTeamRegistrationLabel,
  getTeamRegistrationSummary,
} from '../utils/teamRegistration'
import { useSetVideoTiebreakMutation } from '../store/api/reportsApi'

const STATUS_OPTIONS = [
  { value: 'registered', label: 'Đã đăng ký' },
  { value: 'confirmed', label: 'Đã xác nhận' },
  { value: 'auto_approved', label: 'Tự động duyệt' },
  { value: 'active', label: 'Đã duyệt (hoạt động)' },
  { value: 'completed', label: 'Đã hoàn thành' },
  { value: 'rejected', label: 'Bị từ chối' },
]

const STATUS_COLORS: Record<string, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
  registered: 'info',
  confirmed: 'primary',
  auto_approved: 'success',
  active: 'success',
  completed: 'default',
  rejected: 'error',
}

interface MemberEmail {
  email: string
  userId?: string
  fullName?: string
  role?: string
  isValid?: boolean
  isLoading?: boolean
  error?: string
  pendingAccount?: boolean
}

type MemberCardsMap = Record<string, MemberCardInfo>
type UserSummary = {
  id: string
  email?: string
  full_name?: string
  username?: string
  role?: string
}

const EMPTY_MEMBER_DETAILS: MemberDetails = {
  student_id: '',
  full_name: '',
  date_of_birth: '',
  university: '',
  faculty: '',
  email: '',
  bio: '',
}

const PENDING_PREFIX = 'pending:'

const isRealUserId = (id: string | undefined | null): boolean =>
  !!id && !id.startsWith(PENDING_PREFIX)

const makePendingId = (): string =>
  `${PENDING_PREFIX}${typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`}`

type RegistrationCheck = {
  label: string
  status: 'ok' | 'warn' | 'error'
  message: string
  blocker?: boolean
}

type RegistrationAssessment = {
  rows: RegistrationCheck[]
  validMembers: string[]
  firstError: string | null
  canSubmit: boolean
}

function getErrorMessage(error: unknown, fallback: string): string {
  if (typeof error === 'object' && error !== null) {
    const err = error as {
      data?: { detail?: string }
      error?: string
      message?: string
    }

    return err.data?.detail || err.error || err.message || fallback
  }

  return fallback
}

const isValidEmailFormat = (email: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)

function assessTeamRegistration({
  currentUser,
  editingTeam,
  formData,
  memberEmails,
  memberCards,
  teamProfileFile,
  introVideo,
}: {
  currentUser: { id?: string; role?: string } | null | undefined
  editingTeam: Team | null
  formData: TeamCreate
  memberEmails: MemberEmail[]
  memberCards: MemberCardsMap
  teamProfileFile: TeamFileInfo | null
  introVideo: TeamVideoInfo | null
}): RegistrationAssessment {
  const validMembers: string[] = []
  const emailIssues: string[] = []

  memberEmails.forEach((memberEmail, index) => {
    const trimmedEmail = (memberEmail.email || '').trim()

    if (index === 0) {
      // Đội trưởng: BẮT BUỘC có account thật
      if (memberEmail.userId && isRealUserId(memberEmail.userId)) {
        validMembers.push(memberEmail.userId)
        return
      }
      if (!editingTeam && currentUser?.role === 'student' && currentUser.id) {
        validMembers.push(currentUser.id)
        return
      }
      if (memberEmail.isLoading) {
        emailIssues.push('Đội trưởng đang được xác thực')
        return
      }
      if (memberEmail.error) {
        emailIssues.push(`Đội trưởng: ${memberEmail.error}`)
        return
      }
      if (!trimmedEmail) {
        emailIssues.push('Đội trưởng: chưa nhập email')
        return
      }
      emailIssues.push('Đội trưởng: email chưa được xác thực')
      return
    }

    // Thành viên 2-4: BẮT BUỘC nhập email (theo BR-R1 đoạn 26 — email dùng để cấp
    // tài khoản khi đội vào Bán kết). KHÔNG cần account tồn tại trên hệ thống.
    const trimmedEmail2 = (memberEmail.email || '').trim()
    if (!trimmedEmail2) {
      emailIssues.push(`Thành viên ${index + 1}: chưa nhập email`)
      return
    }
    if (!isValidEmailFormat(trimmedEmail2)) {
      emailIssues.push(`Thành viên ${index + 1}: email không hợp lệ`)
      return
    }
    // Email hợp lệ → gán pendingId để hold profile (không cần account thật).
    const id = memberEmail.userId || makePendingId()
    validMembers.push(id)
  })

  const duplicateMembers = validMembers.length !== new Set(validMembers).size
  const memberCountOk = validMembers.length === 4
  const allCardsPresent = validMembers.every((memberId) => Boolean(memberCards[memberId]))

  const rows: RegistrationCheck[] = [
    {
      label: 'Tên đội',
      status: formData.name.trim() ? 'ok' : 'error',
      message: formData.name.trim() ? 'Đã nhập tên đội' : 'Tên đội là bắt buộc.',
      blocker: true,
    },
    {
      label: 'Trường đại học',
      status: formData.university.trim() ? 'ok' : 'error',
      message: formData.university.trim() ? 'Đã nhập tên trường' : 'Trường đại học là bắt buộc.',
      blocker: true,
    },
    {
      label: 'Thành viên hợp lệ',
      status: emailIssues.length > 0 ? 'error' : 'ok',
      message: emailIssues.length > 0 ? emailIssues.join(' • ') : `Đã xác thực ${validMembers.length} thành viên.`,
      blocker: true,
    },
    {
      label: 'Số thành viên',
      status: memberCountOk ? 'ok' : 'error',
      message: memberCountOk
        ? `Hợp lệ với ${validMembers.length} thành viên.`
        : `Đội phải có ĐÚNG 4 thành viên. Hiện tại: ${validMembers.length}.`,
      blocker: true,
    },
    {
      label: 'Trùng lặp thành viên',
      status: duplicateMembers ? 'error' : 'ok',
      message: duplicateMembers ? 'Không được trùng lặp thành viên.' : 'Không phát hiện trùng lặp.',
      blocker: true,
    },
    {
      label: 'Thẻ sinh viên',
      status: allCardsPresent ? 'ok' : 'error',
      message: allCardsPresent ? 'Đã upload đầy đủ thẻ SV.' : 'Cần upload đủ thẻ SV cho toàn bộ thành viên.',
      blocker: true,
    },
    {
      label: 'Hồ sơ text',
      status: formData.profile_text?.trim() ? 'ok' : 'warn',
      message: formData.profile_text?.trim() ? 'Đã có mô tả hồ sơ đội.' : 'Chưa có mô tả hồ sơ đội. Đây là phần bổ sung.',
      blocker: false,
    },
    {
      label: 'Hồ sơ file',
      status: teamProfileFile ? 'ok' : 'warn',
      message: teamProfileFile ? `Đã upload: ${teamProfileFile.file_name}` : 'Chưa upload file hồ sơ đội.',
      blocker: false,
    },
    {
      // Theo Điều 9.3/9.4: video nộp ở Vòng Sơ loại (R1), không bắt buộc ở đăng ký.
      // Optional ở đây — đội có thể upload sau ở MemorialsPage trong R1.
      label: 'Video giới thiệu đội',
      status: introVideo ? 'ok' : 'warn',
      message: introVideo
        ? `Đã upload: ${introVideo.file_name}`
        : 'Có thể nộp sau ở Vòng Sơ loại (R1). Dùng cho tie-break Điều 10.3.',
      blocker: false,
    },
  ]

  const blocker = rows.find((row) => row.blocker !== false && row.status === 'error')

  return {
    rows,
    validMembers,
    firstError: blocker?.message || null,
    canSubmit: !blocker,
  }
}

export default function TeamsPage() {
  const { user: currentUser } = useAppSelector((state) => state.auth)
  const { data: teams = [], isLoading, refetch } = useGetTeamsQuery()
  const [createTeam, { isLoading: isCreating }] = useCreateTeamMutation()
  const [updateTeam, { isLoading: isUpdating }] = useUpdateTeamMutation()
  const [deleteTeam, { isLoading: isDeleting }] = useDeleteTeamMutation()
  const [uploadMemberCard, { isLoading: isUploadingCard }] = useUploadMemberCardMutation()
  const [uploadTeamProfileFile, { isLoading: isUploadingProfile }] = useUploadTeamProfileFileMutation()
  const [uploadTeamIntroVideo, { isLoading: isUploadingVideo }] = useUploadTeamIntroVideoMutation()
  const [getUserByEmail] = useLazyGetUserByEmailQuery()
  const [fetchUsersBatch] = useFetchUsersBatchMutation()

  const canRegisterTeamPerm = currentUser && hasPermission(currentUser.role, 'REGISTER_TEAM')
  // YC15 (audit 2026-05-23): 1 MSSV chỉ đăng ký được 1 đội. Khi sinh viên đã
  // có đội (kể cả status pending/approved — không gồm rejected/deleted), ẨN
  // nút "Đăng ký đội mới". Staff (canViewAllTeams) vẫn được tạo bình thường.
  const canManageTeam = currentUser && hasPermission(currentUser.role, 'MANAGE_TEAM')
  const canAssignCases = currentUser && hasPermission(currentUser.role, 'ASSIGN_CASES')
  const canManageUsers = currentUser && hasPermission(currentUser.role, 'MANAGE_USERS')
  const canViewAllTeams = canAssignCases || canManageUsers
  const isSecretary = currentUser?.role === 'secretary'

  const userTeam = teams.find((team) => team.members.includes(currentUser?.id || ''))
  const canViewAllTeams_early = (currentUser && (hasPermission(currentUser.role, 'ASSIGN_CASES') || hasPermission(currentUser.role, 'MANAGE_USERS'))) || false
  const userTeamBlocksRegister = !!userTeam && !['rejected', 'deleted'].includes(String((userTeam as any).status || ''))
  const canRegisterTeam = canRegisterTeamPerm && (canViewAllTeams_early || !userTeamBlocksRegister)

  // ─── State declarations — PHẢI đặt trước derived IIFE `displayTeams` ────
  // Bug fix 2026-05-21: trước đây state khai báo SAU displayTeams → TDZ
  // ReferenceError ("Cannot access 'searchTerm' before initialization") khi
  // render lần đầu, crash tab Tạo đội.
  const [openDialog, setOpenDialog] = useState(false)
  const [viewDialog, setViewDialog] = useState(false)
  const [editingTeam, setEditingTeam] = useState<Team | null>(null)
  const [viewingTeam, setViewingTeam] = useState<Team | null>(null)
  // D.1 — search + filter trạng thái + sort theo tên
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [sortBy, setSortBy] = useState<'name_asc' | 'name_desc' | 'created_desc'>('name_asc')
  // D.3 — video viewer dialog (D.2 detailDialog đã xoá — TeamDetailDialog tự quản tab)
  const [videoDialog, setVideoDialog] = useState<{ open: boolean; team: Team | null }>({
    open: false,
    team: null,
  })

  // D.1 — base list theo quyền, sau đó áp filter + sort.
  const baseTeams = (canViewAllTeams ? teams : userTeam ? [userTeam] : []).filter(
    (team) => team.status !== 'deleted'
  )
  const displayTeams = useMemo(() => {
    const q = searchTerm.trim().toLowerCase()
    let arr = baseTeams.filter((t) => {
      const matchSearch =
        !q || [t.name, t.university].some((v) => String(v || '').toLowerCase().includes(q))
      const matchStatus = statusFilter === 'all' || t.status === statusFilter
      return matchSearch && matchStatus
    })
    arr = [...arr].sort((a, b) => {
      if (sortBy === 'name_asc') return a.name.localeCompare(b.name, 'vi')
      if (sortBy === 'name_desc') return b.name.localeCompare(a.name, 'vi')
      // created_desc — fallback so sánh chuỗi (ISO date)
      const da = (a as any).created_at || ''
      const db = (b as any).created_at || ''
      return db.localeCompare(da)
    })
    return arr
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseTeams, searchTerm, statusFilter, sortBy])
  const teamsNeedingAction = displayTeams.filter((team) => {
    const summary = getTeamRegistrationSummary(team)
    return !summary.is_complete || team.status === 'registered' || team.status === 'rejected'
  })
  const missingStudentIdCount = displayTeams.filter((team) => getTeamRegistrationSummary(team).missing_keys.includes('missing_student_id')).length
  const missingProfileCount = displayTeams.filter((team) => getTeamRegistrationSummary(team).missing_keys.includes('team_profile')).length
  const incompleteMembersCount = displayTeams.filter((team) => getTeamRegistrationSummary(team).missing_keys.includes('member_count')).length
  const uploadedProfileCount = displayTeams.filter((team) => getTeamRegistrationSummary(team).has_profile_file).length

  const allUserIds = displayTeams
    .flatMap((team) => team.members.filter(isRealUserId))
    .filter((id, index, arr) => arr.indexOf(id) === index && id)

  const { data: users = [] } = useGetUsersBatchQuery(allUserIds, {
    skip: allUserIds.length === 0 || isLoading,
  })
  const [formData, setFormData] = useState<TeamCreate>({
    name: '',
    university: '',
    members: [],
    profile_text: '',
    profile_file: undefined,
    member_cards: {},
    member_details: {},
  })
  const [memberEmails, setMemberEmails] = useState<MemberEmail[]>([])
  const [memberCards, setMemberCards] = useState<MemberCardsMap>({})
  const [teamProfileFile, setTeamProfileFile] = useState<TeamFileInfo | null>(null)
  const [introVideo, setIntroVideo] = useState<TeamVideoInfo | null>(null)
  const [memberDetailsByIdx, setMemberDetailsByIdx] = useState<Record<number, MemberDetails>>({})
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [submitAttempted, setSubmitAttempted] = useState(false)
  const [snackbar, setSnackbar] = useState<{
    open: boolean
    message: string
    severity: 'success' | 'error' | 'warning' | 'info'
  }>({
    open: false,
    message: '',
    severity: 'info',
  })

  const dialogContentRef = useRef<HTMLDivElement | null>(null)

  const [deleteConfirmTeam, setDeleteConfirmTeam] = useState<Team | null>(null)
  const [tiebreakTeam, setTiebreakTeam] = useState<Team | null>(null)

  const handleConfirmDeleteTeam = async () => {
    if (!deleteConfirmTeam) return
    try {
      await deleteTeam(deleteConfirmTeam.id).unwrap()
      notify(`Đã giải tán đội thi "${deleteConfirmTeam.name}".`, 'success')
      setDeleteConfirmTeam(null)
      refetch()
    } catch (err: any) {
      notify(
        `Giải tán thất bại: ${err?.data?.detail || err?.message || 'Không xác định'}`,
        'error',
      )
    }
  }

  const notify = (message: string, severity: 'success' | 'error' | 'warning' | 'info') => {
    setSnackbar({ open: true, message, severity })

    if (severity === 'error' || severity === 'warning') {
      setErrorMessage(message)
    }

    if (severity === 'success') {
      setSuccessMessage(message)
    }
  }

  useEffect(() => {
    if (!canRegisterTeam && !canViewAllTeams && !userTeam) {
      setErrorMessage('Bạn không có quyền xem danh sách teams. Chỉ có thể xem team của mình.')
    }
  }, [canRegisterTeam, canViewAllTeams, userTeam])

  useEffect(() => {
    if (openDialog && submitAttempted) {
      dialogContentRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }, [openDialog, submitAttempted, errorMessage, successMessage])

  const loadTeamData = async (team: Team) => {
    setEditingTeam(team)
    setFormData({
      name: team.name,
      university: team.university,
      members: team.members,
      profile_text: team.profile_text || '',
      profile_file: team.profile_file || undefined,
      member_cards: team.member_cards || {},
      member_details: team.member_details || {},
    })
    setMemberCards(team.member_cards || {})
    setTeamProfileFile(team.profile_file || null)
    setIntroVideo(team.intro_video || null)

    const detailsByIdx: Record<number, MemberDetails> = {}
    team.members.forEach((memberId, index) => {
      detailsByIdx[index] = {
        ...EMPTY_MEMBER_DETAILS,
        ...(team.member_details?.[memberId] || {}),
      }
    })

    const realIds = team.members.filter(isRealUserId)

    const fillFromUserInfo = (
      fetchedUsers: UserSummary[],
      fallbackUsers: UserSummary[] = []
    ) => {
      const memberEmailsData: MemberEmail[] = team.members.map((memberId, index) => {
        if (isRealUserId(memberId)) {
          const info =
            fetchedUsers.find((u) => u.id === memberId) ||
            fallbackUsers.find((u) => u.id === memberId)
          if (info) {
            const existing = detailsByIdx[index] || { ...EMPTY_MEMBER_DETAILS }
            detailsByIdx[index] = {
              ...existing,
              email: existing.email?.trim() ? existing.email : info.email || '',
              full_name: existing.full_name?.trim()
                ? existing.full_name
                : info.full_name || info.username || '',
            }
            return {
              email: info.email || '',
              userId: info.id,
              fullName: info.full_name || info.username,
              role: info.role,
              isValid: true,
            }
          }
          return { email: '', userId: memberId, isValid: false }
        }
        // Pending profile-only member: lấy email từ member_details
        const detail = team.member_details?.[memberId]
        return {
          email: detail?.email || '',
          userId: memberId,
          fullName: detail?.full_name,
          isValid: !!detail?.email && isValidEmailFormat(detail.email),
        }
      })
      setMemberEmails(memberEmailsData)
      setMemberDetailsByIdx({ ...detailsByIdx })
    }

    try {
      if (realIds.length === 0) {
        fillFromUserInfo([])
        return
      }
      const fetchedUsers = await fetchUsersBatch(realIds).unwrap()
      fillFromUserInfo(fetchedUsers)
    } catch (err: unknown) {
      console.error('Error fetching user info for team members:', err)
      fillFromUserInfo([], users)
    }
  }

  const handleCreate = () => {
    setEditingTeam(null)
    setFormData({
      name: '',
      university: '',
      members: currentUser?.role === 'student' && currentUser.id ? [currentUser.id] : [],
      profile_text: '',
      profile_file: undefined,
      member_cards: {},
      member_details: {},
    })

    if (currentUser?.role === 'student' && currentUser.email && currentUser.id) {
      setMemberEmails([
        {
          email: currentUser.email,
          userId: currentUser.id,
          fullName: currentUser.full_name || currentUser.username,
          role: currentUser.role,
          isValid: true,
        },
      ])
      setMemberDetailsByIdx({
        0: {
          ...EMPTY_MEMBER_DETAILS,
          full_name: currentUser.full_name || currentUser.username || '',
          email: currentUser.email,
        },
      })
    } else {
      setMemberEmails([])
      setMemberDetailsByIdx({})
    }

    setMemberCards({})
    setTeamProfileFile(null)
    setIntroVideo(null)
    setOpenDialog(true)
    setErrorMessage(null)
    setSuccessMessage(null)
    setSubmitAttempted(false)
  }

  const handleUpdate = async (team: Team) => {
    await loadTeamData(team)
    setOpenDialog(true)
    setErrorMessage(null)
    setSuccessMessage(null)
    setSubmitAttempted(false)
  }

  const handleCloseDialog = () => {
    setOpenDialog(false)
    setEditingTeam(null)
    setFormData({
      name: '',
      university: '',
      members: [],
      profile_text: '',
      profile_file: undefined,
      member_cards: {},
      member_details: {},
    })
    setMemberEmails([])
    setMemberCards({})
    setTeamProfileFile(null)
    setIntroVideo(null)
    setMemberDetailsByIdx({})
    setErrorMessage(null)
    setSuccessMessage(null)
    setSubmitAttempted(false)
  }

  const handleView = (team: Team) => {
    setViewingTeam(team)
    setViewDialog(true)
  }

  const handleCloseViewDialog = () => {
    setViewDialog(false)
    setViewingTeam(null)
  }

  const isValidEmail = isValidEmailFormat

  const updateMemberDetail = (index: number, field: keyof MemberDetails, value: string) => {
    setMemberDetailsByIdx((prev) => ({
      ...prev,
      [index]: {
        ...(prev[index] || EMPTY_MEMBER_DETAILS),
        [field]: value,
      },
    }))
  }

  // #27 (audit P3): latest-request-wins cho email lookup slot 0.
  // User nhập email A → fire async, nhập email B → stale response A không
  // được phép overwrite state.
  const _emailReqSeqRef = useRef(0)
  const handleMemberEmailChange = async (index: number, email: string) => {
    if (index === 0 && editingTeam && !isSecretary) return

    const newMemberEmails = [...memberEmails]
    const oldUserId = memberEmails[index]?.userId

    // Slot 1-3: chỉ cần email hợp lệ — KHÔNG cần account trên hệ thống
    if (index > 0) {
      if (!isValidEmail(email)) {
        newMemberEmails[index] = {
          email,
          isValid: false,
          isLoading: false,
          error: email ? 'Email không hợp lệ' : undefined,
        }
        setMemberEmails([...newMemberEmails])
        return
      }
      // Giữ ID cũ nếu là pending, nếu chưa có thì tạo mới
      const memberId = oldUserId && !isRealUserId(oldUserId) ? oldUserId : makePendingId()
      newMemberEmails[index] = {
        email,
        userId: memberId,
        isValid: true,
        isLoading: false,
      }
      const newMembers = [...formData.members]
      newMembers[index] = memberId
      setFormData({ ...formData, members: newMembers })
      setMemberDetailsByIdx((prev) => ({
        ...prev,
        [index]: {
          ...(prev[index] || EMPTY_MEMBER_DETAILS),
          email,
          university: prev[index]?.university || formData.university.trim(),
        },
      }))
      if (oldUserId && oldUserId !== memberId) {
        setMemberCards((prev) => {
          const next = { ...prev }
          delete next[oldUserId]
          return next
        })
      }
      setMemberEmails([...newMemberEmails])
      return
    }

    // Slot 0 (Đội trưởng): BẮT BUỘC có account thật
    newMemberEmails[index] = {
      email,
      isLoading: true,
      isValid: false,
    }
    setMemberEmails([...newMemberEmails])

    if (!isValidEmail(email)) {
      newMemberEmails[index] = {
        email,
        isValid: false,
        isLoading: false,
        error: 'Email không hợp lệ',
      }
      setMemberEmails([...newMemberEmails])
      return
    }

    // #27: bump seq counter — chỉ apply response nếu seq vẫn là latest.
    const mySeq = ++_emailReqSeqRef.current
    try {
      const result = await getUserByEmail(email).unwrap()
      if (mySeq !== _emailReqSeqRef.current) return  // stale → discard

      if (result.role !== 'student') {
        newMemberEmails[index] = {
          email,
          isValid: false,
          isLoading: false,
          error: 'Đội trưởng phải là tài khoản sinh viên',
        }
        setMemberEmails([...newMemberEmails])
        return
      }

      newMemberEmails[index] = {
        email,
        userId: result.id,
        fullName: result.full_name || result.username,
        role: result.role,
        isValid: true,
        isLoading: false,
        pendingAccount: Boolean(result.is_placeholder || !result.is_active),
      }

      const newMembers = [...formData.members]
      newMembers[0] =
        !editingTeam && currentUser?.role === 'student' && currentUser.id
          ? currentUser.id
          : result.id
      setFormData({ ...formData, members: newMembers })

      setMemberDetailsByIdx((prev) => ({
        ...prev,
        [index]: {
          ...(prev[index] || EMPTY_MEMBER_DETAILS),
          full_name: result.full_name || result.username || '',
          email,
          university: prev[index]?.university || formData.university.trim(),
        },
      }))

      if (oldUserId && oldUserId !== result.id) {
        setMemberCards((prev) => {
          const next = { ...prev }
          delete next[oldUserId]
          return next
        })
      }
    } catch (err: unknown) {
      newMemberEmails[index] = {
        email,
        isLoading: false,
        isValid: false,
        error: getErrorMessage(err, 'Đội trưởng cần có tài khoản sinh viên trên hệ thống'),
      }
      setMemberEmails([...newMemberEmails])
      return
    }

    setMemberEmails([...newMemberEmails])
  }

  const handleAddMember = () => {
    if (memberEmails.length >= 4) {
      setSnackbar({
        open: true,
        message: 'Đội tối đa 4 thành viên — không thể thêm.',
        severity: 'warning',
      })
      return
    }
    const newIndex = memberEmails.length
    setMemberEmails([...memberEmails, { email: '', isValid: false }])
    setFormData({ ...formData, members: [...formData.members, ''] })
    setMemberDetailsByIdx((prev) => ({
      ...prev,
      [newIndex]: { ...EMPTY_MEMBER_DETAILS },
    }))
  }

  const handleRemoveMember = (index: number) => {
    if (index === 0 && !editingTeam && currentUser?.role === 'student') return
    if (index === 0 && editingTeam && !isSecretary) return

    const newMemberEmails = memberEmails.filter((_, i) => i !== index)
    const newMembers = formData.members.filter((_, i) => i !== index)
    const removedUserId = memberEmails[index]?.userId

    setMemberEmails(newMemberEmails)
    setFormData({ ...formData, members: newMembers })

    if (removedUserId) {
      setMemberCards((prev) => {
        const next = { ...prev }
        delete next[removedUserId]
        return next
      })
    }

    setMemberDetailsByIdx((prev) => {
      const next: Record<number, MemberDetails> = {}

      Object.entries(prev).forEach(([key, value]) => {
        const currentIndex = Number(key)

        if (currentIndex < index) next[currentIndex] = value
        if (currentIndex > index) next[currentIndex - 1] = value
      })

      return next
    })
  }

  const handleMemberCardUpload = async (member: MemberEmail, file?: File) => {
    if (!file) return

    if (!member.userId) {
      notify('Vui lòng xác thực email thành viên trước khi upload thẻ SV.', 'error')
      return
    }

    if (file.size > 5 * 1024 * 1024) {
      notify(`Thẻ SV vượt quá 5MB (${(file.size / 1024 / 1024).toFixed(1)}MB).`, 'error')
      return
    }

    const lower = file.name.toLowerCase()
    const isPdfOrJpg = lower.endsWith('.pdf') || lower.endsWith('.jpg') || lower.endsWith('.jpeg')

    if (!isPdfOrJpg) {
      notify('Thẻ SV chỉ chấp nhận PDF/JPG.', 'error')
      return
    }

    try {
      const uploaded = await uploadMemberCard({ memberId: member.userId, file }).unwrap()
      setMemberCards((prev) => ({ ...prev, [member.userId as string]: uploaded }))
      notify(`Đã tải thẻ SV cho ${member.fullName || member.email}`, 'success')
    } catch (err: unknown) {
      notify(getErrorMessage(err, 'Upload thẻ SV thất bại'), 'error')
    }
  }

  const handleTeamProfileUpload = async (file?: File) => {
    if (!file) return

    if (file.size > 10 * 1024 * 1024) {
      notify(`Hồ sơ đội vượt quá 10MB (${(file.size / 1024 / 1024).toFixed(1)}MB).`, 'error')
      return
    }

    const lower = file.name.toLowerCase()
    const isAllowed =
      lower.endsWith('.pdf') ||
      lower.endsWith('.docx') ||
      lower.endsWith('.jpg') ||
      lower.endsWith('.jpeg') ||
      lower.endsWith('.png')

    if (!isAllowed) {
      notify('Hồ sơ đội chỉ chấp nhận PDF, DOCX, JPG hoặc PNG.', 'error')
      return
    }

    try {
      const uploaded = await uploadTeamProfileFile({ file }).unwrap()
      setTeamProfileFile(uploaded)
      notify(`Đã tải hồ sơ đội: ${uploaded.file_name}`, 'success')
    } catch (err: unknown) {
      notify(getErrorMessage(err, 'Upload hồ sơ đội thất bại'), 'error')
    }
  }

  const submitInFlightRef = useRef(false)
  const handleSubmit = async () => {
    // #28 (audit P3): mutex chống spam click — guard ngoài loading state
    // (loading state có 1 tick chậm giữa click → mutation start).
    if (submitInFlightRef.current) return
    submitInFlightRef.current = true
    try {
      await _handleSubmitInternal()
    } finally {
      submitInFlightRef.current = false
    }
  }

  const _handleSubmitInternal = async () => {
    setErrorMessage(null)
    setSuccessMessage(null)
    setSubmitAttempted(true)

    const assessment = assessTeamRegistration({
      currentUser,
      editingTeam,
      formData,
      memberEmails,
      memberCards,
      teamProfileFile,
      introVideo,
    })

    if (!assessment.canSubmit) return

    const validMembers = assessment.validMembers

    if (!teamProfileFile) {
      notify('Cần tải file Hồ sơ đội thi trước khi hoàn tất đăng ký.', 'error')
      return
    }

    // Video giới thiệu KHÔNG còn bắt buộc khi đăng ký — đội sẽ nộp ở R1
    // (Điều 9.3/9.4). Nếu có sẵn → gửi kèm; nếu không → optional.

    const cardsForMembers: MemberCardsMap = {}

    for (const memberId of validMembers) {
      const card = memberCards[memberId]

      if (!card) {
        notify('Cần upload đủ thẻ SV cho toàn bộ thành viên trước khi gửi.', 'error')
        return
      }

      cardsForMembers[memberId] = card
    }

    const detailsForMembers: Record<string, MemberDetails> = {}

    for (let index = 0; index < validMembers.length; index += 1) {
      const userId = validMembers[index]
      const details = memberDetailsByIdx[index]

      if (!details) {
        notify(`Vui lòng nhập thông tin đăng ký cho Thành viên ${index + 1}.`, 'error')
        return
      }

      const missing: string[] = []

      if (!details.student_id?.trim()) missing.push('MSSV')
      if (!details.full_name?.trim()) missing.push('Họ tên')
      if (!details.date_of_birth?.trim()) missing.push('Ngày sinh')
      if (!details.university?.trim()) missing.push('Trường')
      if (!details.faculty?.trim()) missing.push('Khoa')
      // Email chỉ bắt buộc cho Đội trưởng (index=0) — họ là người login.
      // 3 thành viên còn lại không cần email (Vòng Bán kết mới cấp tài khoản).
      if (index === 0 && !details.email?.trim()) missing.push('Email')

      if (missing.length > 0) {
        notify(`Thành viên ${index + 1} thiếu trường bắt buộc: ${missing.join(', ')}.`, 'error')
        return
      }

      detailsForMembers[userId] = {
        student_id: details.student_id.trim(),
        full_name: details.full_name.trim(),
        date_of_birth: details.date_of_birth.trim(),
        university: details.university.trim(),
        faculty: details.faculty.trim(),
        email: (details.email || '').trim(),
        bio: (details.bio || '').trim(),
      }
    }

    const finalFormData: TeamCreate = {
      name: formData.name.trim(),
      university: formData.university.trim(),
      members: validMembers,
      profile_text: (formData.profile_text || '').trim() || undefined,
      profile_file: teamProfileFile,
      intro_video: introVideo,
      member_cards: cardsForMembers,
      member_details: detailsForMembers,
    }

    try {
      if (editingTeam) {
        await updateTeam({ id: editingTeam.id, ...finalFormData }).unwrap()
        notify('Cập nhật đội thi thành công!', 'success')
      } else {
        await createTeam(finalFormData).unwrap()
        notify('Đăng ký đội thi thành công!', 'success')
      }

      handleCloseDialog()
      refetch()
    } catch (err: unknown) {
      console.error('[Team Registration Error]', err)
      const action = editingTeam ? 'Cập nhật đội thất bại' : 'Tạo đội thất bại'
      notify(`${action}: ${getErrorMessage(err, 'Có lỗi xảy ra khi đăng ký đội thi.')}`, 'error')
    }
  }

  const registrationAssessment = assessTeamRegistration({
    currentUser,
    editingTeam,
    formData,
    memberEmails,
    memberCards,
    teamProfileFile,
    introVideo,
  })

  return (
    <Container maxWidth="xl" sx={{ mt: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 4 }, px: { xs: 2, sm: 3 } }}>
      <PageHeader
        title="Quản lý Đội thi"
        subtitle="Đăng ký đội, thành viên và trạng thái duyệt theo từng cuộc thi."
        icon={<Groups />}
        actions={
          canRegisterTeam ? (
            <Button
              variant="contained"
              color="primary"
              startIcon={<Add />}
              onClick={handleCreate}
              sx={{
                background: gradientBrand,
                '&:hover': {
                  background: gradientBrand,
                  filter: 'brightness(1.06)',
                },
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                py: 1,
              }}
            >
              Đăng ký đội mới
            </Button>
          ) : null
        }
      />

      {!openDialog && errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage(null)}>
          {errorMessage}
        </Alert>
      )}

      {!openDialog && successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {canViewAllTeams && displayTeams.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <Box>
              <Typography variant="subtitle2" fontWeight={700}>
                Hàng chờ duyệt hồ sơ
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {teamsNeedingAction.length} đội cần kiểm tra hoặc bổ sung trước khi duyệt.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip label={`Thiếu MSSV: ${missingStudentIdCount}`} color={missingStudentIdCount ? 'warning' : 'default'} size="small" />
              <Chip label={`Thiếu hồ sơ đội: ${missingProfileCount}`} color={missingProfileCount ? 'warning' : 'default'} size="small" />
              <Chip label={`Chưa đủ thành viên: ${incompleteMembersCount}`} color={incompleteMembersCount ? 'warning' : 'default'} size="small" />
              <Chip label={`Đã upload profile: ${uploadedProfileCount}`} color="success" variant="outlined" size="small" />
            </Box>
          </Box>
        </Paper>
      )}

      {/* D.1 — sort/filter bar. Student chỉ thấy đội mình → ẩn toàn bộ
          search + filter + sort (không có gì để lọc). */}
      {currentUser?.role !== 'student' && (
        <Paper
          elevation={0}
          sx={(theme) => ({
            mb: 2,
            p: 2,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            display: 'grid',
            gap: 1.5,
            gridTemplateColumns: { xs: '1fr', md: 'minmax(240px, 1fr) 220px 220px' },
          })}
        >
          <TextField
            label="Tìm theo tên đội / trường"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            fullWidth
          />
          <TextField
            label="Trạng thái"
            select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            size="small"
            fullWidth
          >
            <MenuItem value="all">Tất cả trạng thái</MenuItem>
            <MenuItem value="draft">Nháp</MenuItem>
            <MenuItem value="registered">Chờ duyệt</MenuItem>
            <MenuItem value="active">Đã duyệt</MenuItem>
            <MenuItem value="auto_approved">Tự động duyệt</MenuItem>
            <MenuItem value="rejected">Từ chối</MenuItem>
          </TextField>
          <TextField
            label="Sắp xếp"
            select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as any)}
            size="small"
            fullWidth
          >
            <MenuItem value="name_asc">Tên đội A → Z</MenuItem>
            <MenuItem value="name_desc">Tên đội Z → A</MenuItem>
            <MenuItem value="created_desc">Mới đăng ký trước</MenuItem>
          </TextField>
        </Paper>
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={(theme) => getTablePaperSx(theme)}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Tên đội</strong></TableCell>
                <TableCell><strong>Trường đại học</strong></TableCell>
                <TableCell><strong>Số thành viên</strong></TableCell>
                <TableCell><strong>Hồ sơ</strong></TableCell>
                <TableCell><strong>Trạng thái</strong></TableCell>
                <TableCell><strong>Số cuộc thi</strong></TableCell>
                <TableCell align="right"><strong>Thao tác</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayTeams.length === 0 ? (
                <TableRow>


                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      {canRegisterTeam ? 'Chưa có đội thi nào. Hãy đăng ký đội mới!' : 'Bạn chưa tham gia đội thi nào.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                displayTeams.map((team) => {
                  const registrationSummary = getTeamRegistrationSummary(team)

                  return (
                    <TableRow key={team.id} hover>
                      <TableCell>{team.name}</TableCell>
                      <TableCell>{team.university}</TableCell>
                      <TableCell>{team.members.length}</TableCell>
                      <TableCell sx={{ minWidth: 190 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                          <Chip
                            label={getTeamRegistrationLabel(team)}
                            color={getTeamRegistrationColor(team)}
                            size="small"
                            variant={registrationSummary.is_complete ? 'filled' : 'outlined'}
                            sx={{ alignSelf: 'flex-start' }}
                          />
                          <LinearProgress
                            variant="determinate"
                            value={(registrationSummary.required_done / Math.max(1, registrationSummary.required_total)) * 100}
                            color={registrationSummary.is_complete ? 'success' : 'warning'}
                            sx={{ width: 120, height: 6, borderRadius: 1 }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={STATUS_OPTIONS.find((status) => status.value === team.status)?.label || team.status}
                          color={STATUS_COLORS[team.status] || 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{team.assigned_cases?.length || 0}</TableCell>
                      <TableCell align="right">
                        {/* D.2 — gộp icon view + edit thành 1 icon Visibility (mở modal 2 tabs). */}
                        <Tooltip title="Xem / chỉnh sửa">
                          <IconButton size="small" onClick={() => handleView(team)} color="primary">
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                        {/* D.3 — icon video: xem nhanh + tải; bỏ chấm điểm. */}
                        {team.intro_video && (
                          <Tooltip title="Xem / tải video giới thiệu">
                            <IconButton
                              size="small"
                              onClick={() => setVideoDialog({ open: true, team })}
                              color="info"
                            >
                              <Movie fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {isSecretary && team.status !== 'deleted' && (
                          <IconButton
                            size="small"
                            onClick={() => setDeleteConfirmTeam(team)}
                            color="error"
                            title="Giải tán đội thi"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  )
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: { xs: 1, sm: 2 },
            m: { xs: 1, sm: 2 },
          },
        }}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' }, fontWeight: 600 }}>
          {editingTeam ? 'Chỉnh sửa đội thi' : 'Đăng ký đội thi mới'}
        </DialogTitle>

        <DialogContent ref={dialogContentRef} sx={{ px: { xs: 2, sm: 3 } }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: { xs: 1, sm: 2 } }}>
            {openDialog && errorMessage && (
              <Alert severity="error" onClose={() => setErrorMessage(null)}>
                {errorMessage}
              </Alert>
            )}

            {openDialog && successMessage && (
              <Alert severity="success" onClose={() => setSuccessMessage(null)}>
                {successMessage}
              </Alert>
            )}

            {openDialog && submitAttempted && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {registrationAssessment.rows
                  .filter((row) => row.status !== 'ok')
                  .map((row) => (
                    <Alert key={row.label} severity={row.status === 'warn' ? 'warning' : 'error'} variant="outlined">
                      <Typography variant="subtitle2" fontWeight={700}>
                        {row.label}
                      </Typography>
                      <Typography variant="body2">{row.message}</Typography>
                    </Alert>
                  ))}
              </Box>
            )}

            <TextField
              label="Tên đội"
              value={formData.name}
              onChange={(event) => setFormData({ ...formData, name: event.target.value })}
              required
              fullWidth
            />

            <TextField
              label="Trường đại học"
              value={formData.university}
              onChange={(event) => setFormData({ ...formData, university: event.target.value })}
              required
              fullWidth
            />

            <TextField
              label="Mô tả hồ sơ đội (Team Profile)"
              value={formData.profile_text || ''}
              onChange={(event) => setFormData({ ...formData, profile_text: event.target.value })}
              fullWidth
              multiline
              minRows={3}
              helperText="Giới thiệu ngắn về thành tích, định hướng, kỹ năng của đội."
            />

            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                File hồ sơ đội <Typography component="span" color="error.main" fontSize="0.85rem">*</Typography>
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  component="label"
                  variant={teamProfileFile ? 'contained' : 'outlined'}
                  startIcon={<CloudUpload />}
                  disabled={isUploadingProfile}
                  sx={{ textTransform: 'none' }}
                >
                  {isUploadingProfile ? 'Đang tải...' : teamProfileFile ? 'Đổi file hồ sơ' : 'Tải file hồ sơ'}
                  <input
                    hidden
                    type="file"
                    accept=".pdf,.docx,.jpg,.jpeg,.png"
                    onChange={(event) => {
                      handleTeamProfileUpload(event.target.files?.[0])
                      event.target.value = ''
                    }}
                  />
                </Button>

                {teamProfileFile && (
                  <Chip label={teamProfileFile.file_name} size="small" variant="outlined" color="primary" />
                )}
              </Box>

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                Chấp nhận PDF, DOCX, JPG, PNG. Tối đa 10MB. Bắt buộc để hoàn tất đăng ký.
              </Typography>
            </Paper>

            {/* Video giới thiệu — TUỲ CHỌN. Theo Điều 9.3/9.4 video chính thức nộp
                ở Vòng Sơ loại (R1) qua màn hình Đơn khởi kiện. Cho phép upload sẵn
                ở đây để đỡ thao tác. */}
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: introVideo ? 'success.light' : 'divider' }}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Video giới thiệu đội{' '}
                <Typography component="span" color="text.secondary" fontSize="0.8rem">
                  (tuỳ chọn — có thể nộp sau ở Vòng Sơ loại)
                </Typography>
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Theo thể lệ Điều 9.3 / 10.3: video dùng cho tie-break khi hoà điểm
                ở Vòng Sơ loại. Đội có thể upload tại đăng ký hoặc ở phần Đơn khởi kiện sau.
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  component="label"
                  variant={introVideo ? 'contained' : 'outlined'}
                  color={introVideo ? 'success' : 'primary'}
                  startIcon={<CloudUpload />}
                  disabled={isUploadingVideo}
                  sx={{ textTransform: 'none' }}
                >
                  {isUploadingVideo
                    ? 'Đang tải video...'
                    : introVideo
                      ? 'Đổi video giới thiệu'
                      : 'Tải video giới thiệu'}
                  <input
                    hidden
                    type="file"
                    accept="video/mp4,video/webm,video/quicktime,video/x-msvideo,.mp4,.webm,.mov,.avi"
                    onChange={async (event) => {
                      const f = event.target.files?.[0]
                      if (!f) return
                      const MAX = 200 * 1024 * 1024
                      if (f.size > MAX) {
                        notify(`Video vượt quá 200MB (hiện tại ${(f.size / 1024 / 1024).toFixed(1)}MB)`, 'error')
                        return
                      }
                      try {
                        const uploaded = await uploadTeamIntroVideo({ file: f }).unwrap()
                        setIntroVideo(uploaded)
                        notify(`Đã tải video: ${uploaded.file_name}`, 'success')
                      } catch (err: any) {
                        notify(err?.data?.detail || err?.message || 'Upload video thất bại', 'error')
                      }
                      event.target.value = ''
                    }}
                  />
                </Button>
                {introVideo && (
                  <Chip
                    label={`${introVideo.file_name}${
                      introVideo.file_size
                        ? ` · ${(Number(introVideo.file_size) / 1024 / 1024).toFixed(1)}MB`
                        : ''
                    }`}
                    size="small"
                    variant="outlined"
                    color="success"
                    onDelete={() => setIntroVideo(null)}
                  />
                )}
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                MP4 / WebM / MOV / AVI. Tối đa 200MB. Đội thi tự giới thiệu (thành viên, định hướng, lý do tham gia).
                <strong> Bắt buộc để hoàn tất đăng ký.</strong>
              </Typography>
            </Paper>

            <Box>
              <Alert severity="info" sx={{ mb: 1 }}>
                Quy định: ĐÚNG 4 thành viên, không trùng email. Chỉ Đội trưởng cần có tài khoản; 3 thành viên còn lại khai báo profile (sẽ được cấp tài khoản khi đội vào Vòng Bán kết).
              </Alert>

              <Alert severity="warning" sx={{ mb: 1 }}>
                Bắt buộc upload đủ thẻ SV PDF/JPG, tối đa 5MB cho tất cả thành viên trước khi gửi hồ sơ.
              </Alert>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  Thành viên
                </Typography>
                <Button
                  size="small"
                  onClick={handleAddMember}
                  disabled={memberEmails.length >= 4}
                  sx={{ textTransform: 'none', fontWeight: 500 }}
                >
                  {memberEmails.length >= 4 ? 'Đã đủ 4 thành viên' : 'Thêm thành viên'}
                </Button>
              </Box>

              {memberEmails.map((memberEmail, index) => {
                const details = memberDetailsByIdx[index] || EMPTY_MEMBER_DETAILS
                const memberLabel = `Thành viên ${index + 1}${
                  index === 0 && !editingTeam && currentUser?.role === 'student' ? ' (Bạn / Đội trưởng)' : ''
                }${index === 0 && editingTeam ? ' (Quản lý đội)' : ''}`

                return (
                  <Paper key={index} variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                      <Typography variant="subtitle2" fontWeight={600}>
                        {memberLabel}
                      </Typography>

                      {memberEmails.length > 1 &&
                        !(index === 0 && !editingTeam && currentUser?.role === 'student') &&
                        !(index === 0 && editingTeam && !isSecretary) && (
                          <IconButton onClick={() => handleRemoveMember(index)} color="error" size="small">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1, mb: 1.5, alignItems: 'flex-start', flexDirection: { xs: 'column', sm: 'row' } }}>
                      {/* Email BẮT BUỘC cho tất cả thành viên (BR-R1 đoạn 26).
                          - Slot 0 (Đội trưởng): email login hệ thống → onBlur lookup user.
                          - Slot 1-3: email để cấp tài khoản khi vào Bán kết → chỉ validate format. */}
                      <Box sx={{ flex: 1, width: '100%' }}>
                        <TextField
                          label={index === 0 ? 'Email Đội trưởng (đăng nhập hệ thống)' : `Email thành viên ${index + 1}`}
                          type="email"
                          value={memberEmail.email}
                          onChange={(event) => {
                            const newEmails = [...memberEmails]
                            newEmails[index] = { ...memberEmails[index], email: event.target.value }
                            setMemberEmails(newEmails)
                            updateMemberDetail(index, 'email', event.target.value)
                          }}
                          onBlur={(event) => {
                            const value = event.target.value.trim()
                            if (value) handleMemberEmailChange(index, value)
                          }}
                          disabled={
                            !!((index === 0 && !editingTeam && currentUser?.role === 'student') ||
                            (index === 0 && editingTeam && !isSecretary))
                          }
                          required
                          fullWidth
                          error={memberEmail.error !== undefined}
                          helperText={
                            memberEmail.isLoading
                              ? 'Đang tìm kiếm...'
                              : index === 0 && editingTeam && !isSecretary
                                ? 'Chỉ thư ký mới có thể thay đổi người quản lý đội thi'
                                : memberEmail.error ||
                                  (memberEmail.isValid && memberEmail.fullName
                                    ? `${memberEmail.fullName}${memberEmail.role ? ` (${memberEmail.role})` : ''}`
                                    : index === 0
                                      ? 'Đội trưởng phải có tài khoản sinh viên trên hệ thống'
                                      : 'Email thành viên — sẽ cấp tài khoản khi đội vào Vòng Bán kết')
                          }
                          InputProps={{
                            endAdornment: memberEmail.isLoading ? (
                              <CircularProgress size={20} />
                            ) : memberEmail.isValid ? (
                              <CheckCircle color="success" fontSize="small" />
                            ) : memberEmail.error ? (
                              <ErrorIcon color="error" fontSize="small" />
                            ) : null,
                          }}
                        />
                      </Box>

                      <Box sx={{ minWidth: { xs: '100%', sm: 180 } }}>
                        <Button
                          component="label"
                          variant={memberEmail.userId && memberCards[memberEmail.userId] ? 'contained' : 'outlined'}
                          startIcon={<CloudUpload />}
                          size="small"
                          disabled={!memberEmail.userId || isUploadingCard}
                          fullWidth
                          sx={{ textTransform: 'none' }}
                        >
                          {memberEmail.userId && memberCards[memberEmail.userId] ? 'Đã tải thẻ SV' : 'Tải thẻ SV (PDF/JPG)'}
                          <input
                            hidden
                            type="file"
                            accept=".pdf,.jpg,.jpeg"
                            onChange={(event) => {
                              handleMemberCardUpload(memberEmail, event.target.files?.[0])
                              event.target.value = ''
                            }}
                          />
                        </Button>
                      </Box>
                    </Box>

                    <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
                      <TextField label="MSSV" value={details.student_id} onChange={(event) => updateMemberDetail(index, 'student_id', event.target.value)} required fullWidth size="small" />
                      <TextField label="Họ và tên" value={details.full_name} onChange={(event) => updateMemberDetail(index, 'full_name', event.target.value)} required fullWidth size="small" />
                      <TextField label="Ngày sinh" type="date" value={details.date_of_birth} onChange={(event) => updateMemberDetail(index, 'date_of_birth', event.target.value)} required fullWidth size="small" InputLabelProps={{ shrink: true }} />
                      <TextField label="Trường" value={details.university} onChange={(event) => updateMemberDetail(index, 'university', event.target.value)} required fullWidth size="small" />
                      <TextField label="Khoa / Ngành" value={details.faculty} onChange={(event) => updateMemberDetail(index, 'faculty', event.target.value)} required fullWidth size="small" />
                      <TextField
                        label="Giới thiệu ngắn về bản thân"
                        value={details.bio}
                        onChange={(event) => updateMemberDetail(index, 'bio', event.target.value)}
                        fullWidth
                        size="small"
                        multiline
                        minRows={1}
                        maxRows={3}
                        sx={{ gridColumn: { sm: '1 / -1' } }}
                      />
                    </Box>
                  </Paper>
                )
              })}
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: { xs: 1.5, sm: 2 }, gap: 1 }}>
          <Button onClick={handleCloseDialog} sx={{ textTransform: 'none', fontWeight: 500 }}>
            Hủy
          </Button>

          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={isCreating || isUpdating || isUploadingCard || isUploadingProfile || isUploadingVideo}
            sx={{
              background: gradientBrand,
              '&:hover': {
                background: gradientBrand,
                filter: 'brightness(1.06)',
              },
              textTransform: 'none',
              fontWeight: 500,
              borderRadius: 1,
            }}
          >
            {isCreating || isUpdating ? <CircularProgress size={20} /> : editingTeam ? 'Cập nhật' : 'Hoàn tất đăng ký'}
          </Button>
        </DialogActions>
      </Dialog>

      {viewingTeam && (
        <TeamDetailDialog
          open={viewDialog}
          onClose={handleCloseViewDialog}
          team={viewingTeam}
          canReview={!!canAssignCases}
          onReviewed={() => refetch()}
          canEdit={!!((canManageTeam && userTeam?.id === viewingTeam.id) || canAssignCases)}
          onRequestEdit={() => viewingTeam && handleUpdate(viewingTeam)}
        />
      )}

      {/* D.3 — Intro video viewer */}
      <IntroVideoDialog
        open={videoDialog.open}
        onClose={() => setVideoDialog({ open: false, team: null })}
        team={videoDialog.team}
      />

      <Dialog
        open={!!deleteConfirmTeam}
        onClose={() => !isDeleting && setDeleteConfirmTeam(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Giải tán đội thi</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Hành động này KHÔNG thể hoàn tác. Đội thi sẽ bị đánh dấu là đã giải tán
            và không thể tham gia tiếp các vòng thi. Toàn bộ thành viên sẽ
            nhận email thông báo.
          </Alert>
          {deleteConfirmTeam && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Bạn xác nhận giải tán đội:
              </Typography>
              <Typography variant="subtitle1" fontWeight={600}>
                {deleteConfirmTeam.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {deleteConfirmTeam.university} • {deleteConfirmTeam.members?.length || 0} thành viên
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmTeam(null)} disabled={isDeleting}>
            Hủy
          </Button>
          <Button
            onClick={handleConfirmDeleteTeam}
            color="error"
            variant="contained"
            disabled={isDeleting}
          >
            {isDeleting ? <CircularProgress size={20} /> : 'Giải tán đội'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.severity === 'error' ? 8000 : 4000}
        onClose={() => setSnackbar((value) => ({ ...value, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar((value) => ({ ...value, open: false }))}
          sx={{ width: '100%', minWidth: 360 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Tie-break Video clip dialog (Điều 10.3) */}
      <VideoTiebreakDialog
        team={tiebreakTeam}
        onClose={() => setTiebreakTeam(null)}
        onSaved={() => notify('Đã lưu điểm tie-break Video clip.', 'success')}
      />
    </Container>
  )
}

// ════════════════════════════════════════════════════════════════════
// Video Tie-break Dialog (R1→R2 Điều 10.3)
// ════════════════════════════════════════════════════════════════════

function VideoTiebreakDialog({
  team,
  onClose,
  onSaved,
}: {
  team: Team | null
  onClose: () => void
  onSaved: () => void
}) {
  const [creativity, setCreativity] = useState<number>(0)
  const [presentation, setPresentation] = useState<number>(0)
  const [professionalism, setProfessionalism] = useState<number>(0)
  const [err, setErr] = useState<string | null>(null)
  const [setTiebreak, { isLoading }] = useSetVideoTiebreakMutation()

  useEffect(() => {
    if (team) {
      const tb = (team as any).video_tiebreak || {}
      setCreativity(Number(tb.creativity || 0))
      setPresentation(Number(tb.presentation || 0))
      setProfessionalism(Number(tb.professionalism || 0))
      setErr(null)
    }
  }, [team])

  if (!team) return null

  const introVideo = (team as any).intro_video
  const total = creativity + presentation + professionalism

  const handleSave = async () => {
    setErr(null)
    if ([creativity, presentation, professionalism].some((v) => v < 0 || v > 10)) {
      setErr('Mỗi tiêu chí phải trong khoảng 0–10.')
      return
    }
    try {
      await setTiebreak({
        team_id: team.id,
        creativity,
        presentation,
        professionalism,
      }).unwrap()
      onSaved()
      onClose()
    } catch (e: any) {
      setErr(e?.data?.detail || e?.message || 'Lưu thất bại')
    }
  }

  const Score = ({
    label,
    value,
    onChange,
    desc,
  }: {
    label: string
    value: number
    onChange: (v: number) => void
    desc: string
  }) => (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle2" gutterBottom>
        {label}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" mb={1}>
        {desc}
      </Typography>
      <TextField
        type="number"
        size="small"
        fullWidth
        value={value}
        onChange={(e) => onChange(Math.max(0, Math.min(10, Number(e.target.value) || 0)))}
        inputProps={{ min: 0, max: 10, step: 0.5 }}
        helperText="Thang điểm 0–10"
      />
    </Paper>
  )

  return (
    <Dialog open={Boolean(team)} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Movie color="warning" />
          <span>Tie-break Video clip — {team.name}</span>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <b>Điều 10.3 Thể lệ:</b> Khi 2+ đội có tổng điểm Vòng Sơ loại bằng nhau, Ban Giám
            khảo xét tiêu chí phụ qua Video clip giới thiệu — mỗi tiêu chí 0–10đ.
          </Typography>
        </Alert>

        {introVideo?.storage_path ? (
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              📹 Video giới thiệu của đội
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" mb={1}>
              {introVideo.file_name}
            </Typography>
          </Paper>
        ) : (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Đội chưa upload video giới thiệu — tie-break có thể không áp dụng được.
          </Alert>
        )}

        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' } }}>
          <Score
            label="1. Tính sáng tạo"
            value={creativity}
            onChange={setCreativity}
            desc="Cách thức thể hiện nội dung, ý tưởng, dấu ấn riêng"
          />
          <Score
            label="2. Kỹ năng trình bày"
            value={presentation}
            onChange={setPresentation}
            desc="Khả năng diễn đạt, ngôn ngữ, hình ảnh, phương tiện"
          />
          <Score
            label="3. Tính chuyên nghiệp"
            value={professionalism}
            onChange={setProfessionalism}
            desc="Sự chỉn chu, cấu trúc video, mức độ đầu tư chất lượng"
          />
        </Box>

        <Paper sx={{ mt: 2, p: 2, bgcolor: 'background.default', textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            Tổng điểm tie-break
          </Typography>
          <Typography variant="h4" fontWeight={700} color="warning.main">
            {total.toFixed(1)} / 30
          </Typography>
        </Paper>

        {err && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {err}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Đóng</Button>
        <Button variant="contained" onClick={handleSave} disabled={isLoading} startIcon={<Save />}>
          {isLoading ? 'Đang lưu…' : 'Lưu điểm'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

/**
 * D.3 — Intro video viewer dialog.
 * Cho phép xem nhanh + tải file. BỎ flow chấm điểm video (chuyển sang
 * module Bài nộp / Video giới thiệu của Admin).
 */
function IntroVideoDialog({
  open,
  onClose,
  team,
}: {
  open: boolean
  onClose: () => void
  team: Team | null
}) {
  const [streamVideo, { isFetching }] = useLazyStreamTeamIntroVideoQuery()
  const [videoUrl, setVideoUrl] = useState<string | null>(null)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const videoRef = useRef<HTMLVideoElement | null>(null)
  // UX fix (audit 2026-05-21): KHÔNG reset state khi `open=false` — chờ
  // Dialog animate close xong rồi cleanup ở `TransitionProps.onExited` để
  // không "modal rỗng" giữa frame.
  useEffect(() => {
    if (!open || !team?.id) return
    let cancelled = false
    let createdUrl: string | null = null
    streamVideo(team.id)
      .unwrap()
      .then((blob) => {
        if (cancelled) return
        createdUrl = URL.createObjectURL(blob)
        setVideoUrl(createdUrl)
      })
      .catch((err: any) => {
        if (cancelled) return
        // UX fix: map HTTP status → message tiếng Việt cụ thể
        const status = err?.status
        let msg = err?.data?.detail || 'Không tải được video giới thiệu.'
        if (status === 404) msg = 'Đội này chưa có video giới thiệu hoặc file đã bị xoá.'
        else if (status === 403) msg = 'Bạn không có quyền xem video của đội này.'
        else if (status === 401) msg = 'Phiên đăng nhập hết hạn — vui lòng đăng nhập lại.'
        else if (typeof status === 'number' && status >= 500)
          msg = 'Lỗi máy chủ — vui lòng thử lại sau.'
        else if (status === 'FETCH_ERROR') msg = 'Mất kết nối mạng — vui lòng thử lại.'
        setErrorMsg(msg)
      })
    return () => {
      cancelled = true
      // KHÔNG revoke ngay khi `open` change — chờ animate xong (onExited).
      // Tuy nhiên nếu team.id đổi giữa lúc dialog vẫn mở → revoke ngay url cũ.
      if (createdUrl && open) {
        // Hoán bù: chỉ revoke khi createdUrl khác url hiện đang được set state
        // (team change). Cleanup chính ở onExited.
      }
    }
  }, [open, team?.id, streamVideo])

  // Cleanup hoàn toàn sau khi Dialog animate close — gọi từ TransitionProps.onExited
  const handleDialogExited = () => {
    // Pause video element trước khi revoke blob (tránh decode error)
    try {
      videoRef.current?.pause()
      if (videoRef.current) videoRef.current.src = ''
    } catch {
      /* ignore */
    }
    if (videoUrl) {
      URL.revokeObjectURL(videoUrl)
    }
    setVideoUrl(null)
    setErrorMsg(null)
  }

  const handleDownload = () => {
    if (!videoUrl || !team) return
    const a = document.createElement('a')
    a.href = videoUrl
    a.download = team.intro_video?.file_name || `team-${team.name}-intro.mp4`
    document.body.appendChild(a)
    a.click()
    a.remove()
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      TransitionProps={{ onExited: handleDialogExited }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Movie color="info" />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6">Video giới thiệu</Typography>
          {team && (
            <Typography variant="caption" color="text.secondary">
              {team.name}
            </Typography>
          )}
        </Box>
      </DialogTitle>
      <DialogContent>
        {errorMsg ? (
          <Alert severity="error" variant="outlined">
            {errorMsg}
          </Alert>
        ) : isFetching && !videoUrl ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, py: 6 }}>
            <CircularProgress />
            <Typography variant="caption" color="text.secondary">
              Đang tải video...
            </Typography>
          </Box>
        ) : videoUrl ? (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Box
              component="video"
              ref={videoRef}
              src={videoUrl}
              controls
              sx={{ width: '100%', maxHeight: 480, borderRadius: 1, backgroundColor: 'black' }}
            />
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Đội này chưa có video giới thiệu.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Đóng</Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Download />}
          disabled={!videoUrl}
          onClick={handleDownload}
          sx={{ textTransform: 'none' }}
        >
          Tải xuống
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function TeamDetailDialog({
  open,
  onClose,
  team,
  canReview,
  onReviewed,
  canEdit,
  onRequestEdit,
}: {
  open: boolean
  onClose: () => void
  team: Team
  canReview?: boolean
  onReviewed?: () => void
  // D.2 — gộp icon view + edit; modal có 2 tab Info / Chỉnh sửa.
  canEdit?: boolean
  onRequestEdit?: () => void
}) {
  // D.2 — tab state
  const [activeTab, setActiveTab] = useState<'info' | 'edit'>('info')
  useEffect(() => {
    if (open) setActiveTab('info')
  }, [open, team.id])
  const [reviewTeam, { isLoading: reviewing }] = useReviewTeamMutation()
  const [downloadTeamProfile, { isFetching: isDownloadingProfile }] = useLazyDownloadTeamProfileQuery()
  const [rejectReason, setRejectReason] = useState('')
  const [reviewError, setReviewError] = useState<string | null>(null)
  // BR-01-04 ext: auto-review details + revoke
  const { data: autoReview, refetch: refetchAutoReview } = useGetTeamAutoReviewQuery(team.id, {
    skip: !open || !canReview,
  })
  const [revokeAutoApproval, { isLoading: revoking }] = useRevokeAutoApprovalMutation()
  const [revokeReason, setRevokeReason] = useState('')
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false)

  useEffect(() => {
    if (open) {
      setRejectReason('')
      setReviewError(null)
    }
  }, [open, team.id])

  const { data: teamCases = [] } = useGetTeamCasesQuery(team.id, { skip: !open })

  const memberIds = team.members.filter(isRealUserId)
  const { data: users = [] } = useGetUsersBatchQuery(memberIds, {
    skip: !open || memberIds.length === 0,
  })


  const teamMembers = team.members.map((id) =>
    isRealUserId(id) ? users.find((user) => user.id === id) : undefined
  )
  const registrationSummary = getTeamRegistrationSummary(team)
  const registrationIssues = getPriorityRegistrationIssues(team, 8)


  const handleApprove = async () => {
    setReviewError(null)

    try {
      await reviewTeam({ teamId: team.id, decision: 'approve' }).unwrap()
      onReviewed?.()
      onClose()
    } catch (err: unknown) {
      setReviewError(getErrorMessage(err, 'Duyệt thất bại'))
    }
  }

  const handleReject = async () => {
    setReviewError(null)

    const reason = rejectReason.trim()

    if (!reason) {
      setReviewError('Vui lòng nhập lý do từ chối.')
      return
    }

    try {
      await reviewTeam({ teamId: team.id, decision: 'reject', reason }).unwrap()
      onReviewed?.()
      onClose()
    } catch (err: unknown) {
      setReviewError(getErrorMessage(err, 'Từ chối thất bại'))
    }
  }

  const handleDownloadProfile = async () => {
    if (!team.profile_file) return

    setReviewError(null)

    try {
      const blob = await downloadTeamProfile(team.id).unwrap()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')

      link.href = url
      link.download = team.profile_file.file_name || 'team-profile'
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (err: unknown) {
      setReviewError(getErrorMessage(err, 'Tải file hồ sơ đội thất bại'))
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Groups />
          <Typography variant="h6">{team.name}</Typography>
        </Box>
      </DialogTitle>

      {/* D.2 — Tabs Info / Chỉnh sửa */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v as 'info' | 'edit')}>
          <Tab value="info" label="Thông tin đội" />
          {canEdit && <Tab value="edit" label="Chỉnh sửa" />}
        </Tabs>
      </Box>

      <DialogContent sx={{ display: activeTab === 'info' ? 'block' : 'none' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
          {reviewError && (
            <Alert severity="error" onClose={() => setReviewError(null)}>
              {reviewError}
            </Alert>
          )}

          {team.status === 'rejected' && team.rejection_reason && (
            <Alert severity="warning">
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Lý do từ chối gần nhất
              </Typography>
              <Typography variant="body2">{team.rejection_reason}</Typography>
            </Alert>
          )}

          {/* BR-01-04 ext: Auto-review panel cho Admin */}
          {canReview && autoReview && (
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                <Typography variant="subtitle2" fontWeight={700}>
                  Tự động duyệt
                </Typography>
                {!autoReview.feature_flag_enabled && (
                  <Chip size="small" label="Đang TẮT cấp hệ thống" color="default" variant="outlined" />
                )}
                {autoReview.auto_review_outcome === 'passed' && (
                  <Chip size="small" label="Auto-approved" color="success" />
                )}
                {autoReview.auto_review_outcome === 'failed' && (
                  <Chip size="small" label="Không tự duyệt" color="warning" />
                )}
                {autoReview.auto_review_outcome === 'revoked_by_admin' && (
                  <Chip size="small" label="Admin đã huỷ" color="error" />
                )}
                {autoReview.auto_approval_confidence != null && (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`Confidence ${(autoReview.auto_approval_confidence * 100).toFixed(0)}%`}
                  />
                )}
              </Box>
              {autoReview.auto_review_attempted_at && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Chạy lúc: {new Date(autoReview.auto_review_attempted_at).toLocaleString('vi-VN')}
                  {autoReview.auto_approved_at && (
                    <> · Duyệt: {new Date(autoReview.auto_approved_at).toLocaleString('vi-VN')}</>
                  )}
                </Typography>
              )}
              {autoReview.auto_review_reasons && autoReview.auto_review_reasons.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" color="text.secondary" gutterBottom>
                    Lý do không tự duyệt:
                  </Typography>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    {autoReview.auto_review_reasons.map((r, idx) => (
                      <Tooltip
                        key={`${r.code}-${idx}`}
                        title={
                          [r.message, r.member_id ? `Thành viên: ${r.member_id}` : '', r.detail || '']
                            .filter(Boolean).join(' · ')
                        }
                      >
                        <Chip
                          size="small"
                          label={r.code}
                          color={r.code === 'DUPLICATE_MSSV' ? 'error' : 'warning'}
                          variant="outlined"
                        />
                      </Tooltip>
                    ))}
                  </Stack>
                </Box>
              )}
              {team.status === 'auto_approved' && (
                <Box sx={{ mt: 1.5 }}>
                  {autoReview.can_revoke ? (
                    <Button
                      size="small"
                      color="error"
                      variant="outlined"
                      onClick={() => setRevokeDialogOpen(true)}
                    >
                      Huỷ duyệt tự động (Revoke)
                    </Button>
                  ) : (
                    <Tooltip title={autoReview.revoke_blocked_reason || ''}>
                      <span>
                        <Button size="small" variant="outlined" disabled>
                          Đã quá cửa sổ revoke
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                </Box>
              )}
            </Paper>
          )}

          <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Checklist hồ sơ đăng ký
              </Typography>
              <Chip
                label={`${registrationSummary.required_done}/${registrationSummary.required_total} mục bắt buộc`}
                color={registrationSummary.is_complete ? 'success' : 'warning'}
                size="small"
              />
            </Box>
            <LinearProgress
              variant="determinate"
              value={(registrationSummary.required_done / Math.max(1, registrationSummary.required_total)) * 100}
              color={registrationSummary.is_complete ? 'success' : 'warning'}
              sx={{ height: 7, borderRadius: 1, mb: registrationIssues.length ? 1.5 : 0 }}
            />
            {registrationIssues.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                  {registrationIssues.map((item) => (
                    <Chip
                      key={item.key}
                      label={item.label}
                      color={item.severity === 'warning' ? 'warning' : 'error'}
                      size="small"
                      variant="outlined"
                    />
                  ))}
                </Box>
                {registrationIssues.map((item) => (
                  <Typography key={`${item.key}-message`} variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {item.message}
                  </Typography>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="success.main">
                Hồ sơ đã đủ điều kiện duyệt.
              </Typography>
            )}
          </Paper>

          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Trường đại học
            </Typography>
            <Typography variant="body1">{team.university}</Typography>
          </Box>

          {(team.profile_text || team.profile_file) && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Hồ sơ đội
              </Typography>

              {team.profile_text && (
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: team.profile_file ? 1 : 0 }}>
                  {team.profile_text}
                </Typography>
              )}

              {team.profile_file && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Download />}
                  disabled={isDownloadingProfile}
                  onClick={handleDownloadProfile}
                  sx={{ textTransform: 'none' }}
                >
                  {isDownloadingProfile ? 'Đang tải...' : `Tải file: ${team.profile_file.file_name}`}
                </Button>
              )}
            </Box>
          )}

          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Thành viên ({team.members.length} người)
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {team.members.map((memberId, index) => {
                const member = teamMembers[index]
                const details = team.member_details?.[memberId]

                return (
                  <Paper key={memberId || index} variant="outlined" sx={{ p: 1.5, borderRadius: 1 }}>
                    <Typography variant="body2" fontWeight={600}>
                      {member?.full_name || member?.username || details?.full_name || memberId}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {member?.email || details?.email || 'Chưa có email'}
                    </Typography>

                    {details && (
                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1, mt: 1 }}>
                        <Typography variant="caption">MSSV: {details.student_id || '-'}</Typography>
                        <Typography variant="caption">Ngày sinh: {details.date_of_birth || '-'}</Typography>
                        <Typography variant="caption">Trường: {details.university || '-'}</Typography>
                        <Typography variant="caption">Khoa: {details.faculty || '-'}</Typography>
                        <Typography variant="caption" sx={{ gridColumn: { sm: '1 / -1' }, whiteSpace: 'pre-wrap' }}>
                          Giới thiệu: {details.bio || '-'}
                        </Typography>
                      </Box>
                    )}
                  </Paper>
                )
              })}
            </Box>
          </Box>

          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Trạng thái
            </Typography>
            <Chip label={STATUS_OPTIONS.find((status) => status.value === team.status)?.label || team.status} color={STATUS_COLORS[team.status] || 'default'} />
          </Box>

          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Cuộc thi được phân bổ ({teamCases.length})
            </Typography>

            {teamCases.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Chưa có cuộc thi nào được phân bổ
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {teamCases.map((caseItem) => (
                  <Paper key={caseItem.id} sx={{ p: 2 }}>
                    <Typography variant="body2" fontWeight={600}>
                      {caseItem.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Trạng thái: {caseItem.status} | Deadline:{' '}
                      {caseItem.deadline ? new Date(caseItem.deadline).toLocaleDateString('vi-VN') : 'Chưa có'}
                    </Typography>
                  </Paper>
                ))}
              </Box>
            )}
          </Box>

          {canReview && team.status === 'registered' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Duyệt hồ sơ đội (BTC)
              </Typography>

              <TextField
                label="Lý do từ chối (bắt buộc nếu từ chối)"
                value={rejectReason}
                onChange={(event) => setRejectReason(event.target.value)}
                multiline
                minRows={2}
                fullWidth
              />

              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button variant="contained" color="success" disabled={reviewing || !registrationSummary.is_complete} onClick={handleApprove} sx={{ textTransform: 'none' }}>
                  Duyệt
                </Button>
                <Button variant="outlined" color="error" disabled={reviewing} onClick={handleReject} sx={{ textTransform: 'none' }}>
                  Từ chối
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>

      {/* D.2 — Edit tab content */}
      {canEdit && (
        <DialogContent sx={{ display: activeTab === 'edit' ? 'block' : 'none' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2, alignItems: 'flex-start' }}>
            <Alert severity="info" sx={{ width: '100%' }}>
              Mở form chỉnh sửa đầy đủ để cập nhật thông tin đội, thành viên, hồ sơ và video giới thiệu.
            </Alert>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Edit />}
              onClick={() => {
                onClose()
                onRequestEdit?.()
              }}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Mở form chỉnh sửa
            </Button>
          </Box>
        </DialogContent>
      )}

      <DialogActions sx={{ p: { xs: 1.5, sm: 2 } }}>
        <Button onClick={onClose} variant="contained" sx={{ textTransform: 'none', fontWeight: 500, borderRadius: 1 }}>
          Đóng
        </Button>
      </DialogActions>

      {/* BR-01-04 ext: Revoke auto-approval dialog */}
      <Dialog open={revokeDialogOpen} onClose={() => setRevokeDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Huỷ duyệt tự động</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Hồ sơ sẽ trở về trạng thái <b>Đã đăng ký</b> và đợi Admin duyệt thủ công.
            Email thông báo sẽ gửi tới đội.
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Lý do huỷ duyệt (bắt buộc)"
            value={revokeReason}
            onChange={(e) => setRevokeReason(e.target.value)}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevokeDialogOpen(false)}>Đóng</Button>
          <Button
            color="error"
            variant="contained"
            disabled={!revokeReason.trim() || revoking}
            onClick={async () => {
              try {
                await revokeAutoApproval({
                  teamId: team.id,
                  reason: revokeReason.trim(),
                }).unwrap()
                setRevokeDialogOpen(false)
                setRevokeReason('')
                onReviewed?.()
                refetchAutoReview()
              } catch (e: any) {
                setReviewError(e?.data?.detail || e?.message || 'Revoke thất bại')
                setRevokeDialogOpen(false)
              }
            }}
          >
            {revoking ? <CircularProgress size={18} /> : 'Xác nhận huỷ duyệt'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  )
}
